// src/padplanner/ApprovalRoutes.jsx
//
// The library page's Approval routes (S6a2, over S6a1's routes API): the
// org's routes, each a name, a plan type or any type, whether it is the
// default for that type, and 1-10 ordered steps. A step is a role label and
// either a named approver or any member who may approve
// (can_approve_site_plans). Submit copies a route's steps, so changing one
// never changes a submitted revision.
//
// WHO SEES WHAT. Everyone with PadPlanner reads the routes. An org_admin
// with can_edit_site_plans, never through a support session, creates, edits
// and archives them: the server says which (can_manage), and only that
// caller is offered the approver picker (GET …/routes/approvers, the
// resolver 155's trigger is held to). A control the caller cannot use is
// not rendered; the server refuses anyway, and a refusal is shown in its
// words with its code.
//
// ONE DEFAULT PER ORG AND TYPE. Marking a route the default moves the
// default to it (the server does it in one transaction); the form says which
// route it moves from. An archived route is nobody's default and is read-only.
import { useEffect, useState } from "react";
import { SectionLabel } from "./SidePanel";
import { listApprovers } from "../api/padplannerApi";
import { Refusal } from "./PlanReview";
import { PLAN_TYPES, PLAN_TYPE_LABELS } from "./planVocabulary";
import { ANY_APPROVER_TEXT } from "./planLifecycle";

export const MAX_ROUTE_STEPS = 10;   // routes/padplannerApprovalRoutes.mjs MAX_ROUTE_STEPS
export const ROUTES_READ_ONLY_TEXT = "Only an organization admin can change the approval routes.";
export const NO_ROUTES_TEXT = "No approval routes yet. A plan can't be submitted until there is one.";

const field = "rounded border border-bg-border bg-bg px-2 py-1.5 text-[13px] text-fg-primary";
const typeWord = (t) => (t ? PLAN_TYPE_LABELS[t] ?? t : "Any type");

/** The left panel's list. */
export function RouteList({ list, selectedId, onSelect, onCreate, showArchived, onShowArchived }) {
  const routes = list?.routes ?? [];
  const canManage = list?.can_manage === true;
  return (
    <section className="flex flex-col gap-1.5 border-t border-bg-border pt-3" aria-label="Approval routes" data-testid="pp-routes">
      <div className="flex items-center justify-between">
        <SectionLabel>Approval routes</SectionLabel>
        {canManage && <button type="button" className="op-btn op-btn--sm" onClick={onCreate} data-testid="pp-route-new">New route</button>}
      </div>
      {!canManage && <p className="op-meta" data-testid="pp-routes-read-only">{ROUTES_READ_ONLY_TEXT}</p>}
      {routes.length === 0 && <p className="op-meta">{NO_ROUTES_TEXT}</p>}
      <ul className="flex flex-col gap-0.5">
        {routes.map((r) => (
          <li key={r.id}>
            <button type="button" onClick={() => onSelect(r.id)} aria-pressed={r.id === selectedId} data-testid="pp-route-item" data-route-id={r.id}
              className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-[13px] ${r.id === selectedId ? "bg-accent-action/15 text-fg-primary" : "text-fg-primary hover:bg-bg-hover"}`}>
              <span className="min-w-0 truncate">{r.name}</span>
              <span className="op-meta shrink-0">
                {typeWord(r.plan_type)} · {r.steps.length} step{r.steps.length === 1 ? "" : "s"}
                {r.is_default && <span className="op-chip ml-1 px-1 py-0 text-status-ok" data-testid="pp-route-default">Default</span>}
                {r.archived_at && <span className="op-chip ml-1 px-1 py-0">Archived</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <label className="op-meta flex items-center gap-1.5">
        <input type="checkbox" checked={showArchived} onChange={(e) => onShowArchived(e.target.checked)} /> Show archived
      </label>
    </section>
  );
}

/** A route, read; with Edit and Archive for a manager while it is active. */
export function RouteDetail({ route, list, act, onEdit }) {
  const [error, setError] = useState(null);
  const canManage = list?.can_manage === true && !route.archived_at;
  return (
    <div className="flex max-w-xl flex-col gap-3" data-testid="pp-route-detail">
      <div className="flex flex-col gap-1">
        <h2 className="font-display text-[17px] font-semibold text-fg-primary">{route.name}</h2>
        <p className="op-meta">
          {typeWord(route.plan_type)}{route.is_default ? ` · the default for ${route.plan_type ? typeWord(route.plan_type).toLowerCase() : "any type"}` : ""}
          {route.archived_at ? ` · archived${route.archived_by_display_name ? ` by ${route.archived_by_display_name}` : ""}` : ""}
        </p>
      </div>
      <ol className="flex flex-col gap-1" data-testid="pp-route-steps">
        {route.steps.map((s) => (
          <li key={s.ordinal} className="flex gap-2 text-[13px]" data-testid="pp-route-step">
            <span className="op-mono w-5 text-fg-secondary">{s.ordinal}.</span>
            <span className="text-fg-primary">{s.role_label}</span>
            <span className="text-fg-secondary">· {s.approver ? s.approver.display_name ?? "Unknown user" : ANY_APPROVER_TEXT}</span>
          </li>
        ))}
      </ol>
      {canManage && (
        <div className="flex gap-2">
          <button type="button" className="op-btn op-btn--sm" onClick={onEdit}>Edit</button>
          <button type="button" className="op-btn op-btn--sm" data-testid="pp-route-archive" onClick={async () => {
            if (!window.confirm(`Archive ${route.name}? Plans already submitted keep their steps; it is no longer offered or used as a default.`)) return;
            const r = await act({ archive: route.id });
            setError(r.error ?? null);
          }}>Archive</button>
        </div>
      )}
      <Refusal error={error} testid="pp-route-detail-error" />
    </div>
  );
}

const blankStep = () => ({ role_label: "", approver_user_id: null });

/** Create (no route) or edit a route. Steps are sent whole, in order. */
export function RouteForm({ route = null, list, act, onDone, onCancel }) {
  const [name, setName] = useState(route?.name ?? "");
  const [planType, setPlanType] = useState(route?.plan_type ?? "");
  const [isDefault, setIsDefault] = useState(route?.is_default ?? false);
  const [steps, setSteps] = useState(() => (route?.steps?.length
    ? route.steps.map((s) => ({ role_label: s.role_label, approver_user_id: s.approver?.user_id ?? null, display_name: s.approver?.display_name ?? null }))
    : [blankStep()]));
  const [approvers, setApprovers] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listApprovers().then((r) => { if (!cancelled) setApprovers(r.approvers ?? []); })
      .catch((e) => { if (!cancelled) { setApprovers([]); setError(e); } });
    return () => { cancelled = true; };
  }, []);

  // The default this one would take over, if any.
  const otherDefault = isDefault
    ? (list?.routes ?? []).find((r) => r.is_default && !r.archived_at && r.id !== route?.id && (r.plan_type ?? "") === planType) ?? null
    : null;
  const patchStep = (i, patch) => setSteps((ss) => ss.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i, d) => setSteps((ss) => {
    const n = [...ss];
    [n[i], n[i + d]] = [n[i + d], n[i]];
    return n;
  });

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name: name.trim(), plan_type: planType || null, is_default: isDefault,
      steps: steps.map((s) => ({ role_label: s.role_label.trim(), approver_user_id: s.approver_user_id })),
    };
    const r = await act(route ? { update: route.id, body } : { create: body });
    setBusy(false);
    if (r.error) setError(r.error); else onDone(r.value?.route ?? null);
  }

  // A named approver who no longer qualifies stays shown, so saving is the server's call.
  const options = (s) => {
    const list2 = approvers ?? [];
    return s.approver_user_id && !list2.some((a) => a.user_id === s.approver_user_id)
      ? [{ user_id: s.approver_user_id, display_name: `${s.display_name ?? "Unknown user"} (can't approve now)` }, ...list2]
      : list2;
  };

  return (
    <form className="flex max-w-xl flex-col gap-3" onSubmit={save} aria-label={route ? "Edit route" : "New route"} data-testid="pp-route-form">
      <h2 className="font-display text-[17px] font-semibold text-fg-primary">{route ? `Edit ${route.name}` : "New approval route"}</h2>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Name</span>
        <input className={field} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-label="Route name" /></label>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Plan type</span>
        <select className={field} value={planType} onChange={(e) => setPlanType(e.target.value)} aria-label="Plan type">
          <option value="">Any type</option>
          {PLAN_TYPES.map((t) => <option key={t} value={t}>{PLAN_TYPE_LABELS[t]}</option>)}
        </select></label>
      <label className="flex items-center gap-2 text-[13px] text-fg-primary">
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} aria-label="Default route" />
        The default for {typeWord(planType || null).toLowerCase()}
      </label>
      {otherDefault && <p className="op-meta" data-testid="pp-route-default-moves">The default moves here from {otherDefault.name}.</p>}

      <fieldset className="flex flex-col gap-2">
        <legend className="op-label mb-1 uppercase">Steps, in order</legend>
        {steps.map((s, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid="pp-route-form-step">
            <span className="op-mono w-5 text-fg-secondary">{i + 1}.</span>
            <input className={`${field} min-w-0 flex-1`} value={s.role_label} maxLength={60} placeholder="Role, e.g. Drilling engineer"
              onChange={(e) => patchStep(i, { role_label: e.target.value })} aria-label={`Step ${i + 1} role`} />
            <select className={`${field} min-w-0 flex-1`} value={s.approver_user_id ?? ""} aria-label={`Step ${i + 1} approver`}
              onChange={(e) => patchStep(i, { approver_user_id: e.target.value || null, display_name: null })}>
              <option value="">{ANY_APPROVER_TEXT} (anyone with approval rights)</option>
              {options(s).map((a) => <option key={a.user_id} value={a.user_id}>{a.display_name ?? a.email ?? a.user_id}</option>)}
            </select>
            <button type="button" className="op-meta hover:text-fg-primary" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move step ${i + 1} up`}>↑</button>
            <button type="button" className="op-meta hover:text-fg-primary" disabled={i === steps.length - 1} onClick={() => move(i, 1)} aria-label={`Move step ${i + 1} down`}>↓</button>
            <button type="button" className="op-meta hover:text-fg-primary" disabled={steps.length === 1} onClick={() => setSteps((ss) => ss.filter((_, j) => j !== i))}
              aria-label={`Remove step ${i + 1}`}>Remove</button>
          </div>
        ))}
        {steps.length < MAX_ROUTE_STEPS && (
          <button type="button" className="op-btn op-btn--sm self-start" onClick={() => setSteps((ss) => [...ss, blankStep()])}>Add step</button>
        )}
        {approvers && approvers.length === 0 && <p className="op-meta">No member can approve site plans yet; steps can still be “{ANY_APPROVER_TEXT}”.</p>}
      </fieldset>

      <Refusal error={error} testid="pp-route-form-error" />
      <div className="flex gap-2">
        <button type="submit" className="op-btn op-btn--primary op-btn--sm" disabled={busy}>{route ? "Save route" : "Create route"}</button>
        <button type="button" className="op-btn op-btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
